import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ethers } from 'ethers';
import { formatUnits, parseUnits } from 'ethers';
import SmartContractService from '../services/smartContractService';
import { RootState } from './store';
import PolABI from '../abis/PolABI.json' with { type: 'json' };
import NdeipiCoinABI from '../abis/NdeipiTokenABI.json' with { type: 'json' };
import { combineAndDecryptEVMWalletPrivateKey } from '../utils/security';
import axios from 'axios';
import { setSelectedTokenBalance } from './walletSlice';
// import { combineAndDecryptEVMWalletPrivateKey } from '../utils/security';

interface TransactionState {
  status: 'idle' | 'pending' | 'success' | 'failed';
  hash?: string;
  error?: string;
}

interface TokenState {
  contractAddress: string | null;
  signerAddress: string | null;
  owner: string | null;
  minterRole: string | null;
  userBalance: string;
  totalSupply: string;
  cap: string;
  decimals: number;
  name: string;
  symbol: string;
  loading: boolean;
  error: string | null;
  transactionStatus: 'idle' | 'pending' | 'success' | 'failed';
  currentTransaction: TransactionState;
  polBalance: string; 
  polBalanceLoading: boolean;
  gasPrices: any;
}

const initialState: TokenState = {
  contractAddress: null,
  signerAddress: null,
  owner: null,
  minterRole: null,
  userBalance: '0',
  totalSupply: '0',
  cap: '0',
  decimals: 18,
  name: '',
  symbol: '',
  loading: false,
  error: null,
  transactionStatus: 'idle',
   currentTransaction: {
    status: 'idle',
  },
  polBalance: '0', 
  polBalanceLoading: false,
  gasPrices: null,
};

const handleContractCall = async (
  contract: ethers.Contract,
  method: string,
  args: any[] = [],
  dispatch: any,
  successCallback?: () => void
) => {
  try {
    const tx = await contract[method](...args);
    await tx.wait();
    successCallback?.();
    return tx;
  } catch (error: any) {
    dispatch(setError(error.reason || error.message));
    throw error;
  }
};

export const initializeTokenContract = createAsyncThunk(
  'ndeipiCoin/initializeContract',
  async (_, { dispatch, getState }) => {
    try {
      dispatch(setLoading(true));
      
      let contract: ethers.Contract | null = null;
      let signerAddress: string | null = null;

      const { user } = (getState() as RootState).user;

      if (user?.walletAddress && !user?.securityTokenA) {
        // User is connecting using a metamask wallet
        const { isConnected } = await SmartContractService.checkConnection();
        console.log('Ndeipi Token contract initialization status:', isConnected);
        
        if (isConnected) {           
          signerAddress = await SmartContractService.getSignerAddress();
          console.log('Signer address:', signerAddress);
          contract = SmartContractService.getNdeipiTokenContract();
        } else { 
          console.log('User is not connected to a wallet');
          throw new Error('Wallet not connected');
        }
      } else if (user?.walletAddress && user?.securityTokenA) {
        // User is connecting using a private key
        contract = SmartContractService.getNdeipiTokenContract();
        signerAddress = user.walletAddress;
      }

      if (!contract || !signerAddress) {
        throw new Error('Contract not initialized or signer address not found');
      }

      // Split calls into smaller batches
      // Batch 1: Basic token info (name, symbol, decimals)
      const [name, symbol, decimals] = await Promise.all([
        contract.name(),
        contract.symbol(),
        contract.decimals(),
      ]);

      // Batch 2: Supply & cap (totalSupply, cap)
      const [totalSupply, cap] = await Promise.all([
        contract.totalSupply(),
        contract.cap(),
      ]);

      // Batch 3: User-specific & role info (balance, owner, minterRole)
      const [balance, owner, minterRole] = await Promise.all([
        contract.balanceOf(signerAddress),
        contract.owner(),
        contract.MINTER_ROLE(),
      ]);

      dispatch(setContractInfo({
        address: contract.target.toString(),
        signerAddress,
      }));

      dispatch(setTokenInfo({
        userBalance: ethers.formatUnits(balance, decimals).toString(),
        totalSupply: formatUnits(totalSupply, Number(decimals)),
        cap: formatUnits(cap, Number(decimals)),
        decimals: Number(decimals),
        name,
        symbol,
        owner,
        minterRole,
      }));
      
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Token contract initialization failed'));
      throw error;
    } finally {
      dispatch(setLoading(false));
    }
  }
);

export const fetchUserBalance = createAsyncThunk(
  'ndeipiCoin/fetchUserBalance',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const { user } = (getState() as RootState).user;
    const { selectedToken } = (getState() as RootState).wallet;
   
    if (!user?.walletAddress) {
      return rejectWithValue('User wallet address not found');
    }

    try {
      const ndeipiTokenAddress = '0xD8d401df77080Baa60718FA0b1f775eea272ab3b';
      
      // Use Alchemy RPC URL for better performance and reliability
      const alchemyApiKey = process.env.REACT_APP_ALCHEMY_API_KEY || 'iq-VT6krrIFz7KgVd-yiO9TZ-Ytn_ol3';
      const provider = new ethers.AlchemyProvider('matic', alchemyApiKey);

      // const polygonRpcUrl = `https://polygon-rpc.com/?cacheBuster=${Date.now()}`;
      // const provider = new ethers.JsonRpcProvider(polygonRpcUrl);
      
      // Add a block tag to ensure consistent results (latest known block)
      //const blockTag = 'latest'; // or use 'latest' for the most recent block

      const latestBlock = await provider.getBlockNumber();
      const blockTag = latestBlock - 100;      

      // Create token contract instance
      const tokenContract = new ethers.Contract(ndeipiTokenAddress, NdeipiCoinABI, provider);
      
      // Execute both calls in parallel for better performance
      const [balance, decimals] = await Promise.all([
        tokenContract.balanceOf(user.walletAddress, { blockTag }),
        // To fetch decimals dynamically:
        // tokenContract.decimals({ blockTag }),
        18 
      ]);

      // console.log('Ndeipi balance raw value:', balance.toString());
      
      // Format balance with proper decimals
      const formattedBalance = ethers.formatUnits(balance, decimals);     
      if(selectedToken?.id === 'ndeipi'){
        dispatch(setSelectedTokenBalance(formattedBalance));
      }
      
      dispatch(setUserBalance(formattedBalance));
      return formattedBalance;
    } catch (error) {
      console.error('Error fetching Ndeipi balance:', error);
      const message = error instanceof Error ? error.message : 'Failed to fetch Ndeipi balance';
      dispatch(setError(message));
      return rejectWithValue(message);
    }
  }
);

export const fetchPolBalance = createAsyncThunk(
  'ndeipiCoin/fetchPolBalance',
  async (_, { getState, dispatch }) => {
    const { user } = (getState() as RootState).user;
    if (!user?.walletAddress) {
      throw new Error('User wallet address not found');
    }

    dispatch(setPolBalanceLoading(true));

    try {

      const polTokenAddress =  '0x0000000000000000000000000000000000001010';
      const polygonRpcUrl =  'https://polygon-rpc.com/';

      const provider = new ethers.JsonRpcProvider(polygonRpcUrl);     

      // Create token contract instance
      const tokenContract = new ethers.Contract(polTokenAddress, PolABI, provider);
        
        // Get token decimals
        const decimals = 18;//await tokenContract.decimals();
        
        // Query balance
        const balance = await tokenContract.balanceOf(user?.walletAddress);
      
        
        // Format balance with proper decimals
        const formattedBalance = ethers.formatUnits(balance, decimals);     

       dispatch(setPolBalance(formattedBalance));
    } catch (error) {
      dispatch(setError(error instanceof Error ? error.message : 'Failed to fetch POL balance'));
      console.log('Error fetching POL balance:', error);
      throw error;
    }
  }
);

// Token Transfer Functions
export const transferTokens = createAsyncThunk(
  'ndeipiCoin/transferTokens',
  async (
    { recipient, amount, gasPrice }: { recipient: string; amount: string; gasPrice: string },
    { getState, dispatch }
  ) => {
    try {

       dispatch(setTransactionPending({ hash: undefined }));
        
        const userState = (getState() as RootState).user;
        const {user} = userState;

      const ndeipiTokenAddress =  '0xD8d401df77080Baa60718FA0b1f775eea272ab3b';
      const polygonRpcUrl =  'https://polygon-mainnet.infura.io/v3/5648c44cac314ade85e6a09e928a92a9';

      const provider = new ethers.JsonRpcProvider(polygonRpcUrl);
      

      const privateKey = combineAndDecryptEVMWalletPrivateKey([
                    user?.securityTokenA ?? "",
                    user?.verificationCodeB ?? "",
                    user?.accessPhraseC ?? "",
                    user?.identityMarkerD ?? "",
                  ]);

      let signer: ethers.Wallet;

      console.log('User Security Token A:', user?.securityTokenA);

      if(!user?.securityTokenA){
        await SmartContractService.connectWallet();
                  await SmartContractService.initializeContracts();
                    signer = SmartContractService.getNdeipiTokenContract().runner as ethers.Wallet;
        
      }else{
        signer = new ethers.Wallet(privateKey, provider);
      }

      // const polygonGasPrices:any = await fetchPolygonGasPrices();
      const gasPriceFormatted = ethers.parseUnits(gasPrice, 'gwei');
      console.log('Using gas price:', gasPriceFormatted);

      // Create token contract instance
      const tokenContract = new ethers.Contract(ndeipiTokenAddress, NdeipiCoinABI, signer);

      // Get token decimals
      const decimals = 18;//await tokenContract.decimals();

      // Validate recipient address
      if (!ethers.isAddress(recipient)) {
        throw new Error('Invalid recipient address');
      }    
      
      // Parse the amount with proper decimals
      const parsedAmount = parseUnits(amount, decimals);
      
      //console.log(`Transferring ${amount} NDEIPI (${parsedAmount.toString()} smallest units) to ${recipient}`);

      // Estimate gas first
      // const gasEstimate = await tokenContract.transfer.estimateGas(
      //   recipient, 
      //   parsedAmount
      // );
      // console.log('Estimated gas for transfer:', gasEstimate.toString());

      // Send transaction with 20% more gas than estimate
      const tx = await tokenContract.transfer(
        recipient,
        parsedAmount,
         { gasPrice: ethers.parseUnits('1000', 'gwei') } // 20% buffer
      );

      dispatch(setTransactionPending({hash: tx.hash}));

      // Wait for 2 confirmations
      const receipt = await tx.wait(2);
      
      if (receipt.status !== 1) {
        throw new Error('Transaction failed');
      }

      console.log('Transfer successful with hash:', tx.hash);
      dispatch(setTransactionSuccess());
      // Refresh data after confirmation
      await dispatch(refreshTokenData());
      
      // Optional: Check new balances
      const senderBalance = await tokenContract.balanceOf(await user?.walletAddress);
      const recipientBalance = await tokenContract.balanceOf(recipient);

      console.log('New balances - Sender:', formatUnits(senderBalance, decimals), 'Recipient:', formatUnits(recipientBalance, decimals));

      return tx.hash;
    } catch (error) {
      console.error('Transfer failed:', error);
      throw error;
    }
  }
);

export const transferTokensWithData = createAsyncThunk(
  'ndeipiCoin/transferTokensWithData',
  async (
    { recipient, amount, data }: { recipient: string; amount: string; data: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract ) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'transferAndCall',
      [recipient, parseUnits(amount, ndeipiCoin.decimals), data],
      dispatch,
      () => dispatch(refreshTokenData())
    );
  }
);

export const fetchPolygonGasPrices = createAsyncThunk(
  'gas/fetchPolygonGasPrices',
  async (_, { rejectWithValue }) => {
    try {
      console.log('Fetching Polygon gas prices...');
      const apiKey = process.env.REACT_APP_POLYGONSCAN_API_KEY || 'YOUR_POLYGONSCAN_API_KEY';
      const response = await axios.get(
        `https://api.polygonscan.com/api?module=gastracker&action=gasoracle&apikey=${apiKey}`
      );
      return response.data.result;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);



// Approval Functions
export const approveSpender = createAsyncThunk(
  'ndeipiCoin/approveSpender',
  async (
    { spender, amount }: { spender: string; amount: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'approve',
      [spender, parseUnits(amount, ndeipiCoin.decimals)],
      dispatch
    );
  }
);

export const approveSpenderWithCall = createAsyncThunk(
  'ndeipiCoin/approveSpenderWithCall',
  async (
    { spender, amount, data }: { spender: string; amount: string; data?: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    const method = data ? 'approveAndCall' : 'approve';
    const args = data 
      ? [spender, parseUnits(amount, ndeipiCoin.decimals), data]
      : [spender, parseUnits(amount, ndeipiCoin.decimals)];

    await handleContractCall(
      ndeipiCoinContract,
      method,
      args,
      dispatch
    );
  }
);

// Minting Functions (only for owner/minter)
export const mintTokens = createAsyncThunk(
  'ndeipiCoin/mintTokens',
  async (
    { account, amount }: { account: string; amount: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'mint',
      [account, parseUnits(amount, ndeipiCoin.decimals)],
      dispatch,
      () => dispatch(refreshTokenData())
    );
  }
);

export const finishMinting = createAsyncThunk(
  'ndeipiCoin/finishMinting',
  async (_, { getState, dispatch }) => {
   
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'finishMinting',
      [],
      dispatch
    );
  }
);

// Burning Functions
export const burnTokens = createAsyncThunk(
  'ndeipiCoin/burnTokens',
  async (
    { amount }: { amount: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'burn',
      [parseUnits(amount, ndeipiCoin.decimals)],
      dispatch,
      () => dispatch(refreshTokenData())
    );
  }
);

export const burnTokensFrom = createAsyncThunk(
  'ndeipiCoin/burnTokensFrom',
  async (
    { account, amount }: { account: string; amount: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'burnFrom',
      [account, parseUnits(amount, ndeipiCoin.decimals)],
      dispatch,
      () => dispatch(refreshTokenData())
    );
  }
);

// Allowance Functions
export const increaseAllowance = createAsyncThunk(
  'ndeipiCoin/increaseAllowance',
  async (
    { spender, addedValue }: { spender: string; addedValue: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'increaseAllowance',
      [spender, parseUnits(addedValue, ndeipiCoin.decimals)],
      dispatch
    );
  }
);

export const decreaseAllowance = createAsyncThunk(
  'ndeipiCoin/decreaseAllowance',
  async (
    { spender, subtractedValue }: { spender: string; subtractedValue: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'decreaseAllowance',
      [spender, parseUnits(subtractedValue, ndeipiCoin.decimals)],
      dispatch
    );
  }
);

// Ownership Functions
export const transferOwnership = createAsyncThunk(
  'ndeipiCoin/transferOwnership',
  async (
    { newOwner }: { newOwner: string },
    { getState, dispatch }
  ) => {
    
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'transferOwnership',
      [newOwner],
      dispatch,
      () => dispatch(refreshTokenData())
    );
  }
);

export const renounceOwnership = createAsyncThunk(
  'ndeipiCoin/renounceOwnership',
  async (_, { getState, dispatch }) => {
    
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'renounceOwnership',
      [],
      dispatch,
      () => dispatch(refreshTokenData())
    );
  }
);

// Role Management Functions
export const grantMinterRole = createAsyncThunk(
  'ndeipiCoin/grantMinterRole',
  async (
    { account }: { account: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.minterRole) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'grantRole',
      [ndeipiCoin.minterRole, account],
      dispatch
    );
  }
);

export const revokeMinterRole = createAsyncThunk(
  'ndeipiCoin/revokeMinterRole',
  async (
    { account }: { account: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.minterRole) throw new Error('Contract not initialized');

    await handleContractCall(
      ndeipiCoinContract,
      'revokeRole',
      [ndeipiCoin.minterRole, account],
      dispatch
    );
  }
);



// Data Refresh Functions
export const refreshTokenData = createAsyncThunk(
  'ndeipiCoin/refreshTokenData',
  async (_, { getState, dispatch }) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
    const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();

    console.log('Refreshing Ndeipi Coin data...');

    // Early exit if missing critical data
    if (!ndeipiCoinContract || !ndeipiCoin.signerAddress || !ndeipiCoin.decimals) {
      console.warn('Skipping refresh: Missing contract, signer address, or decimals');
      return;
    }

    const fetchWithRetry = async (call: () => Promise<any>, retries = 3) => {
  for (let i = 0; i < retries; i++) {
    try {
      return await call();
    } catch (error) {
      if (i === retries - 1) throw error;
      await new Promise(resolve => setTimeout(resolve, 1000 * (i + 1)));
    }
  }
};

    try {
      // Split calls into two smaller batches
      // Batch 1: User-specific data (balance)
      const balance = await fetchWithRetry(
  () => ndeipiCoinContract.balanceOf(ndeipiCoin.signerAddress)
);

console.log('User balance fetched:', balance.toString());
console.log('Signer address:', ndeipiCoin.signerAddress);

      // Batch 2: Global token data (totalSupply, cap, owner)
      const [totalSupply, cap, owner] = await Promise.all([
        ndeipiCoinContract.totalSupply(),
        ndeipiCoinContract.cap(),
        ndeipiCoinContract.owner(),
      ]);

      // Update state
      dispatch(setTokenInfo({
        userBalance: formatUnits(balance, ndeipiCoin.decimals),
        totalSupply: formatUnits(totalSupply, ndeipiCoin.decimals),
        cap: formatUnits(cap, ndeipiCoin.decimals),
        owner,
      }));

    } catch (error: any) {
      // Enhanced error handling
      const errorMessage = error.reason || 
                         error.data?.message || 
                         error.message || 
                         'Failed to refresh token data';
      
      console.error('Refresh error:', errorMessage);
      dispatch(setError(errorMessage));

      
    }
  }
);

export const checkAllowance = createAsyncThunk(
  'ndeipiCoin/checkAllowance',
  async (
    { owner, spender }: { owner: string; spender: string },
    { getState, dispatch }
  ) => {
    const { ndeipiCoin } = getState() as { ndeipiCoin: TokenState };
     const ndeipiCoinContract = SmartContractService.getNdeipiTokenContract();
    if (!ndeipiCoinContract || !ndeipiCoin.decimals) throw new Error('Contract not initialized');

    try {
      const allowance = await ndeipiCoinContract.allowance(owner, spender);
      return formatUnits(allowance, ndeipiCoin.decimals);
    } catch (error: any) {
      dispatch(setError(error.reason || error.message));
      throw error;
    }
  }
);

export const swapTokens = createAsyncThunk(
  'ndeipiCoin/swapTokens',
  async (swapData: {
    fromToken: string;
    toToken: string;
    amount: string;
    slippage: string;
  }, { rejectWithValue }) => {
    try {
      // Implement your swap logic here
      // This is a mock implementation
      console.log('Swapping:', swapData);
      
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      
      return {
        hash: '0x' + Math.random().toString(16).substring(2, 66),
        amount: swapData.amount,
        fromToken: swapData.fromToken,
        toToken: swapData.toToken,
      };
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

const ndeipiCoinSlice = createSlice({
  name: 'ndeipiCoin',
  initialState,
  reducers: {
      setTransactionPending: (state, action: PayloadAction<{hash?: string}>) => {
    state.currentTransaction = {
      status: 'pending',
      hash: action.payload.hash
    };
  },
  setTransactionSuccess: (state) => {
    state.currentTransaction.status = 'success';
  },
  setTransactionFailed: (state, action: PayloadAction<string>) => {
    state.currentTransaction = {
      status: 'failed',
      error: action.payload
    };
  },
  resetTransactionState: (state) => {
    state.currentTransaction = {
      status: 'idle'
    };
  },
    setContractInfo(state, action: PayloadAction<{
      address: string;
      signerAddress: string;
    }>) {
      state.contractAddress = action.payload.address;
      state.signerAddress = action.payload.signerAddress;
    },
    setTokenInfo(state, action: PayloadAction<{
  userBalance?: string | bigint;
  totalSupply?: string | bigint;
  cap?: string | bigint;
  decimals?: number | bigint;
  name?: string;
  symbol?: string;
  owner?: string;
  minterRole?: string;
}>) {
  const payload = {
    ...action.payload,
    userBalance: action.payload.userBalance !== undefined 
      ? action.payload.userBalance.toString() 
      : state.userBalance,
    totalSupply: action.payload.totalSupply !== undefined 
      ? action.payload.totalSupply.toString() 
      : state.totalSupply,
    cap: action.payload.cap !== undefined 
      ? action.payload.cap.toString() 
      : state.cap,
    decimals: action.payload.decimals !== undefined 
      ? Number(action.payload.decimals) 
      : state.decimals,
  };

  if (action.payload.name !== undefined) state.name = action.payload.name;
  if (action.payload.symbol !== undefined) state.symbol = action.payload.symbol;
  if (action.payload.owner !== undefined) state.owner = action.payload.owner;
  if (action.payload.minterRole !== undefined) state.minterRole = action.payload.minterRole;
  
  state.userBalance = payload.userBalance;
  state.totalSupply = payload.totalSupply;
  state.cap = payload.cap;
  state.decimals = payload.decimals;
},
    setLoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setError(state, action: PayloadAction<string | null>) {
      state.error = typeof action.payload === 'string' ? action.payload : String(action.payload);
    },
    setTransactionStatus(state, action: PayloadAction<'idle' | 'pending' | 'success' | 'failed'>) {
      state.transactionStatus = action.payload;
    },
    setPolBalanceLoading(state, action: PayloadAction<boolean>) {
      state.polBalanceLoading = action.payload;
    },
    setPolBalance(state, action: PayloadAction<string>) {
      state.polBalance = action.payload;
      state.polBalanceLoading = false; 
    },
    setUserBalance(state, action: PayloadAction<string>) {
      state.userBalance = action.payload;
    },
    resetTokenState: () => initialState
  },
  extraReducers: (builder) => {
    builder
      .addCase(initializeTokenContract.pending, (state) => {
        state.loading = true;
      })
      .addCase(initializeTokenContract.fulfilled, (state) => {
        state.loading = false;
        state.error = null;
      })
      .addCase(initializeTokenContract.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to initialize contract';
      })
      .addCase(fetchPolygonGasPrices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchPolygonGasPrices.fulfilled, (state, action) => {
        state.loading = false;
        state.gasPrices = action.payload;
      })
      .addCase(fetchPolygonGasPrices.rejected, (state, action) => {
        state.loading = false;
        state.error = typeof action.payload === 'string' ? action.payload : String(action.payload);
      })
      ;
  }
});

export const { 
  setContractInfo,
  setTokenInfo,
  setLoading,
  setError,
  setTransactionStatus,
  resetTokenState,
  setTransactionPending,
  setTransactionSuccess,
  setTransactionFailed,
  resetTransactionState,
  setPolBalance,
  setUserBalance,
  setPolBalanceLoading,
} = ndeipiCoinSlice.actions;

export default ndeipiCoinSlice.reducer;